import type {
  DevAutoCalcLogFilter,
  DevEventInboxFilter,
  DevPriceRowFilter,
} from '@/api';
import { getApiClient } from '@/api/client';
import { normalizeApiError } from '@/utils/error-utils';
import type { NormalizedError } from '@/utils/error-utils';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

/** How often the dashboard reloads its data while the tab is visible. */
export const DEV_DASHBOARD_REFRESH_MS = 10_000;

const DEV_DASHBOARD_QUERY_KEY = ['devDashboard'] as const;
const SETTINGS_QUERY_KEY = [...DEV_DASHBOARD_QUERY_KEY, 'settings'] as const;

const liveQueryOptions = {
  refetchInterval: DEV_DASHBOARD_REFRESH_MS,
  refetchIntervalInBackground: false,
  retry: false,
} as const;

export const useDevSettings = () =>
  useQuery({
    queryKey: SETTINGS_QUERY_KEY,
    queryFn: () => getApiClient().getDevDashboardSettings(),
    ...liveQueryOptions,
  });

export type PriceRowsKind = 'priceEvents' | 'bwPrices';

/**
 * The newest price rows (at most 50) that match the filter. With an empty filter, all queries of the
 * same kind share one cache entry. The previous rows stay visible while a new filter loads.
 */
export const useDevPriceRows = (
  kind: PriceRowsKind,
  filter: DevPriceRowFilter,
) =>
  useQuery({
    queryKey: [...DEV_DASHBOARD_QUERY_KEY, kind, filter],
    queryFn: () =>
      kind === 'priceEvents'
        ? getApiClient().searchDevDashboardPriceEvents({
            devPriceRowFilter: filter,
          })
        : getApiClient().searchDevDashboardBwPrices({
            devPriceRowFilter: filter,
          }),
    placeholderData: keepPreviousData,
    ...liveQueryOptions,
  });

/**
 * The newest event inbox messages (at most 50) that match the filter. Works like useDevPriceRows.
 */
export const useDevEventInbox = (filter: DevEventInboxFilter) =>
  useQuery({
    queryKey: [...DEV_DASHBOARD_QUERY_KEY, 'eventInbox', filter],
    queryFn: () =>
      getApiClient().searchDevDashboardEventInbox({
        devEventInboxFilter: filter,
      }),
    placeholderData: keepPreviousData,
    ...liveQueryOptions,
  });

export const useDevIngestionStatus = () =>
  useQuery({
    queryKey: [...DEV_DASHBOARD_QUERY_KEY, 'ingestionStatus'],
    queryFn: () => getApiClient().getDevDashboardIngestionStatus(),
    ...liveQueryOptions,
  });

export const useDevOverview = () =>
  useQuery({
    queryKey: [...DEV_DASHBOARD_QUERY_KEY, 'overview'],
    queryFn: () => getApiClient().getDevDashboardOverview(),
    ...liveQueryOptions,
  });

export const useDevUsers = () =>
  useQuery({
    queryKey: [...DEV_DASHBOARD_QUERY_KEY, 'users'],
    queryFn: () => getApiClient().getDevDashboardUsers(),
    ...liveQueryOptions,
  });

export const useDevAutoCalcRuns = () =>
  useQuery({
    queryKey: [...DEV_DASHBOARD_QUERY_KEY, 'autoCalcRuns'],
    queryFn: () => getApiClient().getDevDashboardAutoCalcRuns(),
    ...liveQueryOptions,
  });

/** All fail logs of one run that match the filter. Disabled until a run is selected. */
export const useDevAutoCalcLogs = (filter: DevAutoCalcLogFilter | undefined) =>
  useQuery({
    queryKey: [...DEV_DASHBOARD_QUERY_KEY, 'autoCalcLogs', filter],
    queryFn: () =>
      getApiClient().searchDevDashboardAutoCalcLogs({
        devAutoCalcLogFilter: filter,
      }),
    enabled: filter !== undefined,
    placeholderData: keepPreviousData,
    ...liveQueryOptions,
  });

export const useDevBackendErrors = () =>
  useQuery({
    queryKey: [...DEV_DASHBOARD_QUERY_KEY, 'backendErrors'],
    queryFn: () => getApiClient().getDevDashboardTelemetryErrors(),
    ...liveQueryOptions,
  });

export const useUpdateDevSetting = (
  onSuccess: () => void,
  onError: (error: NormalizedError) => void,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) =>
      getApiClient().updateDevDashboardSetting({
        key,
        updateDevSettingRequest: { value },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: SETTINGS_QUERY_KEY });
      onSuccess();
    },
    onError: (err) => onError(normalizeApiError(err)),
  });
};
